import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BdsIcon, BdsAutocomplete } from '@/core/constants/webComponents';
import { fetchActivePages, Page } from '@/core/services/PageService';
import { CLASSIFICATION_EVENTS } from '@/features/classification/constants/events';
import './ReplyModal.scss';

const MAX_CHARS = 274;

interface ReplyModalProps {
  platform: string;
  onClose: () => void;
  onSend: (pageId: string, text: string) => void;
}

export const ReplyModal: React.FC<ReplyModalProps> = ({ platform, onClose, onSend }) => {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const selectRef = useRef<HTMLBdsAutocompleteElement>(null);
  const [pages, setPages] = useState<Page[]>([]);
  const [selectedPageId, setSelectedPageId] = useState('');
  const [text, setText] = useState('');
  const [isSending, setIsSending] = useState(false);

  const remaining = MAX_CHARS - text.length;

  // Open dialog + backdrop/escape handling
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (!dialog.open) {
      try {
        dialog.showModal();
      } catch {
        dialog.setAttribute('open', 'true');
      }
    }
    const handleCancel = (e: Event) => { e.preventDefault(); onClose(); };
    dialog.addEventListener('cancel', handleCancel);
    return () => dialog.removeEventListener('cancel', handleCancel);
  }, [onClose]);

  // Fetch pages for the platform
  useEffect(() => {
    fetchActivePages(platform)
      .then(setPages)
      .catch(() => setPages([]));
  }, [platform]);

  // Listen for reply success/error from warroom
  useEffect(() => {
    const handleSuccess = () => {
      setIsSending(false);
      onClose();
    };
    const handleError = () => {
      setIsSending(false);
    };
    document.addEventListener(CLASSIFICATION_EVENTS.NOTIFY_REPLY_SUCCESS, handleSuccess);
    document.addEventListener(CLASSIFICATION_EVENTS.NOTIFY_REPLY_ERROR, handleError);
    return () => {
      document.removeEventListener(CLASSIFICATION_EVENTS.NOTIFY_REPLY_SUCCESS, handleSuccess);
      document.removeEventListener(CLASSIFICATION_EVENTS.NOTIFY_REPLY_ERROR, handleError);
    };
  }, [onClose]);

  // bds-autocomplete fires a native bdsChange CustomEvent
  useEffect(() => {
    const select = selectRef.current;
    if (!select) return;
    const handleChange = (e: Event) => {
      const value = (e as CustomEvent).detail?.value;
      if (value) setSelectedPageId(value);
    };
    select.addEventListener('bdsChange', handleChange);
    return () => select.removeEventListener('bdsChange', handleChange);
  }, []);

  const handleSend = () => {
    if (!selectedPageId || !text.trim() || remaining < 0 || isSending) return;
    setIsSending(true);
    onSend(selectedPageId, text.trim());
  };

  return (
    <dialog
      ref={dialogRef}
      className="reply-modal__overlay"
      data-testid="reply-modal"
      aria-label={t('replyModal.title')}
    >
      <div className="reply-modal">
        {/* Close button */}
        <button
          className="reply-modal__close"
          data-testid="reply-modal-close"
          onClick={onClose}
          aria-label={t('replyModal.close')}
        >
          <BdsIcon name="close" size="medium" />
        </button>

        <div className="reply-modal__content">
          <div className="reply-modal__heading">
            <span className="reply-modal__title">{t('replyModal.title')}</span>

            <BdsAutocomplete
              ref={selectRef}
              class="reply-modal__select"
              data-testid="reply-modal-page-select"
              placeholder={t('replyModal.selectPagePlaceholder')}
              selection-type="single"
              options={JSON.stringify(pages.map((page) => ({ value: page.id, label: page.title || page.username })))}
            />
          </div>

          {/* Textarea + character counter */}
          <div className="reply-modal__input-section">
            <textarea
              className="reply-modal__textarea"
              data-testid="reply-modal-textarea"
              placeholder={t('replyModal.textPlaceholder')}
              value={text}
              maxLength={MAX_CHARS}
              onChange={(e) => setText(e.target.value)}
            />
            <div className="reply-modal__caption">
              <span
                className="reply-modal__char-counter"
                data-testid="reply-modal-char-counter"
              >
                {remaining} {t('replyModal.charsRemaining')}
              </span>
            </div>
          </div>
        </div>

        {/* Action area */}
        <div className="reply-modal__actions">
          <button
            className="reply-modal__btn reply-modal__btn--primary"
            data-testid="reply-modal-send-btn"
            onClick={handleSend}
            disabled={!selectedPageId || !text.trim() || remaining < 0 || isSending}
            title={t('replyModal.send')}
          >
            {isSending ? t('replyModal.sending') : t('replyModal.send')}
          </button>
        </div>
      </div>
    </dialog>
  );
};
